import type { Patient } from '../types';

export type PatientStatusLabel = 'Ativo' | 'Inativo';

/**
 * Status efetivo do paciente. Registros antigos (anteriores ao campo `status`)
 * não têm o campo preenchido e são tratados como ativos.
 */
export function patientStatus(patient: Pick<Patient, 'status'>): PatientStatusLabel {
  return patient.status === 'Inativo' ? 'Inativo' : 'Ativo';
}

export function isActivePatient(patient: Pick<Patient, 'status'>): boolean {
  return patientStatus(patient) === 'Ativo';
}

/**
 * Pacientes inativos (arquivados) não devem aparecer no painel nem ser
 * selecionáveis em agendamentos, gravações ou análises.
 */
export function selectablePatients<T extends Pick<Patient, 'status'>>(patients: T[]): T[] {
  return (patients ?? []).filter(isActivePatient);
}

type RoleAware = { id?: string; role?: string };

/** O papel "profissional" só enxerga os pacientes atribuídos a ele. */
export function isProfessionalRole(role?: string): boolean {
  const normalized = (role || '').toLowerCase();
  if (normalized.includes('administrador')) return false;
  return (
    normalized.includes('profissional') ||
    normalized.includes('psicólog') ||
    normalized.includes('psicolog') ||
    normalized.includes('terapeut')
  );
}

export function isReceptionistRole(role?: string): boolean {
  const normalized = (role || '').toLowerCase();
  return (
    normalized.includes('recepcionista') ||
    normalized.includes('recep') ||
    normalized.includes('secretária') ||
    normalized.includes('secretaria')
  );
}

/**
 * Filtra a carteira de pacientes visível para o perfil ativo.
 * Administrador e recepcionista veem todos; profissional vê apenas os seus.
 */
export function patientsVisibleToProfile<T extends Pick<Patient, 'assignedProfessionalId'>>(
  patients: T[],
  profile: RoleAware | null | undefined,
): T[] {
  if (!isProfessionalRole(profile?.role)) return patients ?? [];
  return (patients ?? []).filter((p) => p.assignedProfessionalId === profile?.id);
}
